/**
 * Fetch and select today's puzzle.
 *
 * The full puzzles.json ships in /public — we fetch once, validate against
 * Zod, then pick the puzzle whose date matches the active Melbourne date.
 *
 * Fallback: if no puzzle for today's date exists (e.g. the pool is short or
 * a refresh has been delayed), select the most recent past puzzle. This keeps
 * the game playable even if the GitHub Action ran late.
 */

import { puzzlesFileSchema, type ParsedPuzzlesFile, type ParsedPuzzle } from '@/schemas/puzzle.schema';
import { activePuzzleDate } from './melbourne';

export interface LoadedPuzzle {
  puzzle: ParsedPuzzle;
  fallback: boolean; // true when the active date wasn't in the pool
}

let cachedFile: ParsedPuzzlesFile | null = null;

export async function loadPuzzlesFile(url = '/puzzles.json'): Promise<ParsedPuzzlesFile> {
  if (cachedFile) return cachedFile;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`puzzle fetch failed: ${res.status}`);
  const json = await res.json();
  const parsed = puzzlesFileSchema.parse(json);
  cachedFile = parsed;
  return parsed;
}

export function selectForDate(file: ParsedPuzzlesFile, date: string): LoadedPuzzle {
  // Filter to live (Steve-approved) puzzles only. In tests/dev mode the file
  // may include unapproved puzzles — we exclude them from the live pool.
  const live = file.puzzles.filter((p) => p.steve_approved);
  const exact = live.find((p) => p.date === date);
  if (exact) return { puzzle: exact, fallback: false };
  // Fallback: most recent past puzzle.
  const past = live.filter((p) => p.date <= date).sort((a, b) => (a.date < b.date ? 1 : -1));
  if (past[0]) return { puzzle: past[0], fallback: true };
  // Last resort: use the first available puzzle (e.g. dev with future-dated
  // pool). Test runs may hit this.
  if (live[0]) return { puzzle: live[0], fallback: true };
  // No live puzzles at all — fall back to first raw puzzle.
  if (file.puzzles[0]) return { puzzle: file.puzzles[0], fallback: true };
  throw new Error('no puzzles available');
}

export async function loadTodaysPuzzle(url = '/puzzles.json', nowMs?: number): Promise<LoadedPuzzle> {
  const file = await loadPuzzlesFile(url);
  return selectForDate(file, activePuzzleDate(nowMs));
}

/** Reset the in-memory cache (for tests). */
export function resetPuzzleCache(): void {
  cachedFile = null;
}
